// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Click-to-define terms.
 *
 * The audience knows most of these. "Containment", "longest match" and
 * "boilerplate filter" are the three the tool is arguing about, so they are
 * defined where they are used rather than in a modal somebody has to find.
 */

export const GLOSSARY: Readonly<Record<string, string>> = {
  'n-gram':
    'A run of N consecutive words. evalspill compares files by asking whether any run of N words from an eval row also occurs somewhere in the training file. Words, not characters — at thirteen characters, more than half of completely unrelated document pairs share a run, and the "shared passage" it shows you is a stopword phrase.',
  containment:
    'The fraction of an eval row\'s words that appear somewhere in the training file as part of a matching run. Normalised by the EVAL row, never by the pair — which is the whole reason this is not built on similarity. A hundred-word question sitting verbatim inside a thousand-word training document has a Jaccard similarity of 0.089 and a containment of 1.00.',
  'longest match':
    'The longest unbroken run of an eval row\'s words that also appears in the training file. This is the number to look at: it is the passage you can read and judge for yourself, and in published comparisons it was the best performing of the four overlap metrics.',
  decontamination:
    'Removing from an evaluation set the examples that also appear in training data, so that the score measures what the model learned rather than what it memorised. The recipe here follows GPT-3\'s: lowercase, whitespace-delimited words, no punctuation, N chosen from the length of your own eval rows.',
  'boilerplate filter':
    'Ignoring word runs that occur in a large number of different training records. A shared instruction template, a licence header or a formatting scaffold matches everything and means nothing. GPT-3 dropped runs occurring in more than ten training documents for exactly this reason; evalspill uses ten, or a thousandth of your corpus, whichever is larger.',
  'training example limit':
    'Nothing to do with this tool — mentioned only because it is a different thing that also gets called a limit. See the sibling tool epochcost.',
  'quasi-verbatim':
    'A row that is not an exact copy but shares a long passage — the same question with a sentence added, or the same paragraph inside a longer document. This is the case exact-match checkers miss and the case this tool is built for.',
  'Wilson interval':
    'A confidence interval for a proportion that behaves sensibly when the count is small or near 100%, where the ordinary normal approximation reports certainty it has not earned. Used here to decide whether two subset scores are actually distinguishable before showing you a difference between them.',
  'document frequency':
    'How many distinct training records contain a given run of words. A run that appears in one record is evidence; a run that appears in twelve thousand is a template.',
};

let tooltip: HTMLElement | null = null;

function hide(): void {
  tooltip?.remove();
  tooltip = null;
}

function show(anchor: HTMLElement, term: string): void {
  hide();
  const definition = GLOSSARY[term];
  if (!definition) return;

  const node = document.createElement('div');
  node.className = 'glossary-tip';
  node.setAttribute('role', 'tooltip');

  const heading = document.createElement('strong');
  heading.textContent = term;
  const body = document.createElement('p');
  body.textContent = definition;
  node.append(heading, body);
  document.body.appendChild(node);

  const box = anchor.getBoundingClientRect();
  const width = node.offsetWidth;
  const left = Math.min(Math.max(8, box.left), window.innerWidth - width - 8);
  const above = box.top > node.offsetHeight + 16;
  node.style.left = `${left}px`;
  node.style.top = above ? `${box.top - node.offsetHeight - 8}px` : `${box.bottom + 8}px`;
  tooltip = node;
}

/**
 * Wire the whole document once.
 *
 * Delegated from the document rather than bound per span, because the interface
 * re-renders and re-binding on every render leaks listeners.
 */
export function mountGlossary(): void {
  document.addEventListener('click', (ev) => {
    const target = (ev.target as HTMLElement | null)?.closest?.('.glossary-link') as HTMLElement | null;
    if (target) {
      const term = target.dataset.term;
      if (term) {
        if (tooltip && tooltip.dataset.term === term) hide();
        else {
          show(target, term);
          if (tooltip) tooltip.dataset.term = term;
        }
        ev.stopPropagation();
        return;
      }
    }
    hide();
  });

  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') hide();
  });

  window.addEventListener('resize', hide, { passive: true });
  window.addEventListener('scroll', hide, { passive: true, capture: true });
}

/**
 * A term the reader can click.
 *
 * A `<button>` rather than a styled `<span>`: a span with a click handler is
 * invisible to the keyboard and to a screen reader, and a sibling tool shipped
 * exactly that and had to fix it.
 */
export function term(text: string, key = text): HTMLButtonElement {
  const node = document.createElement('button');
  node.type = 'button';
  node.className = 'glossary-link';
  node.dataset.term = key;
  node.textContent = text;
  node.setAttribute('aria-label', `${text} — what this means`);
  return node;
}
